import { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { FaEye, FaEyeSlash, FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useSidebar } from "../../App";
import SuccessModal from "../../components/modals/SuccessModal";
import { useNotificationModal } from "../../hooks/useNotificationModal";
import CountryCodeSelect from "../../components/CountryCodeSelect";

import "../../styles/RegisterPage.css";

const BASE_URL = process.env.REACT_APP_API_URL;

const sidebarOptionsList = [
  {
    badge: "Product Management",
    label: "Create and manage product categories",
    value: "category",
  },
  {
    label: "Create and manage styles with attributes",
    value: "style",
  },
  {
    label: "Create and manage raw materials and inventory",
    value: "raw_materials",
  },
  {
    badge: "Merchandising",
    label: "Create and manage purchase orders",
    value: "merchandising_po",
  },
  {
    label: "Create and manage POs for raw materials",
    value: "merchandising_raw_materials_po",
  },
  {
    badge: "Warehouse",
    label: "Goods Receipt Note - Record incoming inventory",
    value: "warehouse_grn",
  },
  {
    label: "Good Receipt Note for raw materials",
    value: "raw_materials_grn",
  },
  {
    label: "Issue inventory for orders",
    value: "warehouse_issuing",
  },
  {
    label: "Monitor inventory levels",
    value: "warehouse_stock",
  },
  {
    label: "Create and manage product discount offers",
    value: "warehouse_offers",
  },
  {
    label: "Manage Stock allocations",
    value: "warehouse_stock_allocation",
  },
  {
    badge: "Finance",
    label: "Manage company currencies and exchange rates",
    value: "finance_currency",
  },
  {
    label: "Register and manage supplier information",
    value: "finance_supplier",
  },
  {
    label: "Configure tax rates and tax calculations",
    value: "accounting_tax",
  },
  {
    badge: "Approvals",
    label: "Review and approve purchase orders",
    value: "approve_po",
  },
  {
    label: "Review and approve POs for raw materials",
    value: "approve_raw_materials_po",
  },
  {
    badge: "Sites & Locations",
    label: "Manage plants and plant managers",
    value: "manage_plants",
  },
  {
    label: "Manage Branches and Branch Managers",
    value: "manage_branches",
  },
  {
    label: "Manage Warehouse Locations",
    value: "manage_warehouse_locations",
  },
  {
    badge: "Settings",
    label: "Manage company website settings and configurations",
    value: "settings_website",
  },
  {
    label: "Manage company profile and information",
    value: "settings_company",
  },
  {
    label: "Code format settings",
    value: "settings_code_generation",
  },
];

export default function RegisterPage() {
  const { id } = useParams();
  useSidebar();
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+94",
    role: "",
    password: "",
    confirmPassword: "",
  });

  const [phoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });
  const [showRules, setShowRules] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setErrorMsg] = useState("");
  const [sideBarOptions, setSideBarOptions] = useState([]);
  const [companyRoles, setCompanyRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const { userData } = useContext(AuthContext);

  const navigate = useNavigate();
  const company_code = userData?.company_code;

  // Fetch company roles based on company_code
  useEffect(() => {
    if (!company_code) {
      setCompanyRoles([]);
      return;
    }

    setLoadingRoles(true);

    const fetchCompanyRoles = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/admin/companies-by-code/${company_code}`,
        );
        const data = await response.json();

        if (data.success && data.company && data.company.roles) {
          // roles is already an array from the backend
          const roles = Array.isArray(data.company.roles)
            ? data.company.roles
            : [];
          setCompanyRoles(roles);
        } else {
          setCompanyRoles([]);
        }
      } catch (error) {
        console.error("Error fetching company roles:", error);
        setCompanyRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchCompanyRoles();
  }, [company_code]);

  // Fetch admin data if editing
  useEffect(() => {
    if (!id) return;

    let isMounted = true;
    setIsLoading(true);

    const fetchAdminData = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/admin/auth/get-admin/${id}`,
        );
        const data = await response.json();

        if (!isMounted) return;

        if (data.success) {
          setFormData({
            name: data.admin.name || "",
            email: data.admin.email || "",
            phone: data.admin.phone_number || "",
            countryCode: data.admin.country_code || "+94",
            role: data.admin.role || "",
            password: "",
            confirmPassword: "",
          });
          setSideBarOptions(
            Array.isArray(data.admin.side_bar_options)
              ? data.admin.side_bar_options
              : [],
          );
        } else {
          const errorMessage = data.message || "Admin not found";
          setErrorMsg(errorMessage);
          setTimeout(() => {
            if (isMounted) setErrorMsg("");
          }, 5000);
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching admin data:", error);
        setErrorMsg("Server error loading admin.");
        setTimeout(() => {
          if (isMounted) setErrorMsg("");
        }, 5000);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchAdminData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleSidebarOptionChange = (option) => {
    setSideBarOptions((prev) =>
      prev.includes(option)
        ? prev.filter((o) => o !== option)
        : [...prev, option],
    );
  };

  // Handle Select / Deselect All
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all options
      const allOptions = sidebarOptionsList.map((opt) => opt.value);
      setSideBarOptions(allOptions);
    } else {
      // Deselect all
      setSideBarOptions([]);
    }
  };

  // Check if all options are selected
  const isAllSelected =
    sidebarOptionsList.length > 0 &&
    sideBarOptions.length === sidebarOptionsList.length;

  // prevent entering numbers or symbols to name field
  const handleNameChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^a-zA-Z\s]/g, "");
    setFormData((prev) => ({ ...prev, name: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "password") {
      const rules = {
        length: value.length >= 8 && value.length <= 20,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /\d/.test(value),
        specialChar: /[\W_]/.test(value),
      };
      setPasswordRules(rules);

      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setPasswordError("Passwords do not match");
      } else {
        setPasswordError("");
      }
    }

    if (name === "confirmPassword") {
      if (value !== formData.password) {
        setPasswordError("Passwords do not match");
      } else {
        setPasswordError("");
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!id && formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords do not match");
      setTimeout(() => setPasswordError(""), 5000);
      return;
    }
    setIsLoading(true);
    try {
      let response, data;
      if (id) {
        // Edit mode (no change)
        const cleanPhone = formData.phone.replace(/\D/g, "");

        response = await fetch(
          `${BASE_URL}/api/admin/auth/update-admin/${id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              country_code: formData.countryCode,
              phone_number: cleanPhone,
              role: formData.role,
              side_bar_options: JSON.stringify(sideBarOptions),
            }),
          },
        );
        data = await response.json();
        if (response.ok && data.success) {
          showSuccess("Admin updated successfully!", "Success");
          setFormData({
            name: "",
            email: "",
            phone: "",
            countryCode: "+94",
            role: "",
            password: "",
            confirmPassword: "",
          });
          setSideBarOptions([]); // Clear sidebar options
          setPasswordError("");
          setTimeout(() => navigate("/users"), 1200);
        } else {
          const errorMessage = data.message || "Update failed";
          showError(errorMessage, "Error");
        }
      } else {
        // Register mode with email verification
        const cleanPhone = formData.phone.replace(/\D/g, "");

        const adminFormData = new FormData();
        adminFormData.append("company_code", company_code);
        adminFormData.append("name", formData.name);
        adminFormData.append("email", formData.email);
        adminFormData.append("country_code", formData.countryCode);
        adminFormData.append("phone", cleanPhone);
        adminFormData.append("role", formData.role);
        adminFormData.append("password", formData.password);
        adminFormData.append(
          "side_bar_options",
          JSON.stringify(sideBarOptions),
        );
        adminFormData.append("frontend_url", window.location.origin);

        response = await fetch(
          `${BASE_URL}/api/admin/auth/create-company-admin`,
          {
            method: "POST",
            body: adminFormData,
          },
        );
        data = await response.json();
        if (response.ok && data.success) {
          const message = data.message?.toLowerCase().includes("verification")
            ? `A verification email has been sent to ${formData.email}.`
            : "User registered successfully!";
          showSuccess(message, "Success");
          setFormData({
            name: "",
            email: "",
            phone: "",
            countryCode: "+94",
            role: "",
            password: "",
            confirmPassword: "",
          });
          setSideBarOptions([]); // Clear sidebar options
          setPasswordError("");
        } else {
          const errorMessage = data.message || "Registration failed";
          showError(errorMessage, "Error");
        }
      }
    } catch (error) {
      showError(error.message || "Something went wrong", "Error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-container mt-4" >
      <Container fluid style={{ maxWidth: "100%", paddingTop: "25px" }}>
        <Card
          className="register-card"
          style={{
            width: "100%",
            maxWidth: "none",
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Card.Body style={{ padding: "30px" }}>
            <div
              className="register-flex-row"
              style={{
                display: "flex",
                gap: "40px",
                alignItems: "flex-start",
              }}
            >
              <div
                className="register-form-section"
                style={{
                  flex:
                    formData.role !== "VCM_Admin" &&
                    formData.role !== "Company_Admin"
                      ? "2"
                      : "1",
                  minWidth: "0",
                }}
              >
                <div style={{ marginBottom: "20px" }}>
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/users")}
                    style={{ padding: "10px" }}
                  >
                    <FaArrowLeft />
                    Back
                  </Button>
                </div>
                <Form className="register-form" onSubmit={handleSubmit}>
                  <h2
                    className="register-title"
                    style={{
                      marginBottom: "30px",
                      color: "#333",
                      fontWeight: "600",
                    }}
                  >
                    {id ? "Edit User" : "Add New User"}
                  </h2>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="name">Full Name</Form.Label>
                        <Form.Control
                          id="name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleNameChange}
                          placeholder="Enter full name"
                          autoComplete="name"
                          required
                          style={{
                            borderRadius: "8px",
                            border: "2px solid #e0e0e0",
                            padding: "12px 16px",
                            fontSize: "16px",
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="email">Email Address</Form.Label>
                        <Form.Control
                          id="email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                          autoComplete="email"
                          required
                          style={{
                            borderRadius: "8px",
                            border: "2px solid #e0e0e0",
                            padding: "12px 16px",
                            fontSize: "16px",
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="phone">Phone Number</Form.Label>
                        <div className="d-flex gap-2">
                          <CountryCodeSelect
                            value={formData.countryCode}
                            onChange={(code) =>
                              setFormData((prev) => ({
                                ...prev,
                                countryCode: code,
                              }))
                            }
                            placeholder="Code"
                            isRequired={true}
                            width="100px"
                          />
                          <Form.Control
                            type="text"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={(e) => {
                              // Only allow digits
                              const value = e.target.value.replace(/\D/g, "");
                              setFormData((prev) => ({
                                ...prev,
                                phone: value,
                              }));
                            }}
                            placeholder="Enter phone number"
                            autoComplete="tel"
                            required
                            style={{
                              flex: 1,
                              height: "48px",
                              fontSize: "16px",
                              borderRadius: "8px",
                              backgroundColor: "#fff",
                              border: "2px solid #e0e0e0",
                            }}
                          />
                        </div>
                        <Form.Text className="text-muted">
                          Enter phone number with country code (e.g., +94 for
                          Sri Lanka)
                        </Form.Text>
                        {phoneError && (
                          <div className="invalid-feedback d-block">
                            {phoneError}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="role">Role</Form.Label>
                        {loadingRoles ? (
                          <div className="text-center py-2">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">Loading roles...</span>
                          </div>
                        ) : companyRoles.length > 0 ? (
                          <Form.Select
                            id="role"
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            required
                            style={{
                              borderRadius: "8px",
                              border: "2px solid #e0e0e0",
                              padding: "12px 16px",
                              fontSize: "16px",
                            }}
                          >
                            <option value="">Select a role</option>
                            {companyRoles.map((role, index) => (
                              <option key={index} value={role}>
                                {role}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <Form.Control
                            type="text"
                            id="role"
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            required
                            placeholder="Enter role"
                            autoComplete="organization-title"
                            style={{
                              borderRadius: "8px",
                              border: "2px solid #e0e0e0",
                              padding: "12px 16px",
                              fontSize: "16px",
                            }}
                          />
                        )}
                        {!loadingRoles && companyRoles.length === 0 && (
                          <Form.Text className="text-muted">
                            No roles defined for this company. Please enter
                            manually or contact your administrator.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {!id && (
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3 position-relative">
                          <Form.Label htmlFor="password">Password</Form.Label>
                          <div style={{ position: "relative" }}>
                            <Form.Control
                              id="password"
                              type={showPassword ? "text" : "password"}
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder="Enter password"
                              autoComplete="new-password"
                              onFocus={() => setShowRules(true)}
                              onBlur={() => setShowRules(false)}
                              required
                              style={{
                                borderRadius: "8px",
                                border: "2px solid #e0e0e0",
                                padding: "12px 40px 12px 16px",
                                fontSize: "16px",
                              }}
                            />
                            <span
                              onClick={() => setShowPassword(!showPassword)}
                              style={{
                                position: "absolute",
                                top: "50%",
                                right: "14px",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                color: "#888",
                                fontSize: "20px",
                                zIndex: 2,
                              }}
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                  setShowPassword(!showPassword);
                              }}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                          </div>
                          {showRules && (
                            <div
                              className="password-rules"
                              style={{
                                marginTop: "10px",
                                padding: "10px",
                                backgroundColor: "#f8f9fa",
                                borderRadius: "6px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <small>Password requirements:</small>
                              <ul
                                className="list-unstyled ms-2"
                                style={{ marginTop: "5px" }}
                              >
                                <li
                                  style={{
                                    color: passwordRules.length
                                      ? "#28a745"
                                      : "#dc3545",
                                  }}
                                >
                                  {passwordRules.length ? "✅" : "❌"} 8-12
                                  characters
                                </li>
                                <li
                                  style={{
                                    color: passwordRules.uppercase
                                      ? "#28a745"
                                      : "#dc3545",
                                  }}
                                >
                                  {passwordRules.uppercase ? "✅" : "❌"} At
                                  least one uppercase letter
                                </li>
                                <li
                                  style={{
                                    color: passwordRules.lowercase
                                      ? "#28a745"
                                      : "#dc3545",
                                  }}
                                >
                                  {passwordRules.lowercase ? "✅" : "❌"} At
                                  least one lowercase letter
                                </li>
                                <li
                                  style={{
                                    color: passwordRules.number
                                      ? "#28a745"
                                      : "#dc3545",
                                  }}
                                >
                                  {passwordRules.number ? "✅" : "❌"} At least
                                  one number
                                </li>
                                <li
                                  style={{
                                    color: passwordRules.specialChar
                                      ? "#28a745"
                                      : "#dc3545",
                                  }}
                                >
                                  {passwordRules.specialChar ? "✅" : "❌"} At
                                  least one special character
                                </li>
                              </ul>
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3 position-relative">
                          <Form.Label htmlFor="confirmPassword">
                            Confirm Password
                          </Form.Label>
                          <div style={{ position: "relative" }}>
                            <Form.Control
                              id="confirmPassword"
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                              placeholder="Confirm your password"
                              autoComplete="new-password"
                              isInvalid={!!passwordError}
                              required
                              style={{
                                borderRadius: "8px",
                                border: "2px solid #e0e0e0",
                                padding: "12px 40px 12px 16px",
                                fontSize: "16px",
                              }}
                            />
                            <span
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              style={{
                                position: "absolute",
                                top: "50%",
                                right: "14px",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                color: "#888",
                                fontSize: "20px",
                                zIndex: 2,
                              }}
                              aria-label={
                                showConfirmPassword
                                  ? "Hide confirm password"
                                  : "Show confirm password"
                              }
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                  setShowConfirmPassword(!showConfirmPassword);
                              }}
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                          </div>
                          {passwordError && (
                            <Form.Control.Feedback
                              type="invalid"
                              style={{ display: "block" }}
                            >
                              {passwordError}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  <div className="text-center" style={{ marginTop: "30px" }}>
                    <Button
                      type="submit"
                      className="register-btn"
                      disabled={isLoading}
                      style={{
                        backgroundColor: "#007bff",
                        border: "none",
                        borderRadius: "8px",
                        padding: "12px 40px",
                        fontSize: "16px",
                        fontWeight: "600",
                      }}
                    >
                      {isLoading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          {id ? "Updating..." : "Registering..."}
                        </>
                      ) : id ? (
                        "Update User"
                      ) : (
                        "Register User"
                      )}
                    </Button>
                  </div>
                </Form>
              </div>

              {formData.role !== "VCM_Admin" &&
                formData.role !== "Company_Admin" && (
                  <div
                    className="sidebar-options-section"
                    style={{
                      flex: "1",
                      minWidth: "300px",
                      backgroundColor: "#f8f9fa",
                      padding: "25px",
                      borderRadius: "12px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Form.Group className="mb-3">
                      <Form.Label className="sidebar-label">
                        <h3
                          style={{
                            fontSize: "20px",
                            fontWeight: "600",
                            color: "#333",
                            marginBottom: "10px",
                          }}
                        >
                          Select Responsibilities
                        </h3>
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#666",
                            marginBottom: "20px",
                            fontWeight: "normal",
                          }}
                        >
                          Choose which modules and features this user can
                          access.
                        </p>
                      </Form.Label>

                      {/* Select All Option */}
                      <div
                        style={{
                          marginBottom: "5px",
                        }}
                      >
                        <Form.Check
                          id="option-select-all"
                          type="checkbox"
                          label="All Responsibilities"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          style={{
                            fontSize: "13px",
                          }}
                        />
                      </div>

                      <div>
                        {sidebarOptionsList.map((opt, index) => (
                          <div key={opt.value} className="mb-2">
                            {opt.badge && (
                              <div
                                style={{
                                  fontWeight: "600",
                                  fontSize: "14px",
                                  color: "#444",
                                  marginTop: index !== 0 ? "10px" : "0",
                                  marginBottom: "4px",
                                }}
                              >
                                {opt.badge}
                              </div>
                            )}

                            <Form.Check
                              id={`option-${opt.value}`}
                              type="checkbox"
                              label={opt.label}
                              checked={sideBarOptions.includes(opt.value)}
                              onChange={() =>
                                handleSidebarOptionChange(opt.value)
                              }
                              style={{
                                fontSize: "13px",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </Form.Group>
                  </div>
                )}
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        message={successMessage}
        title={successTitle}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        message={errorMessage}
        title={errorTitle}
        variant="error"
      />
    </div>
  );
}
